import { type CalendarView, DEFAULT_TIMEZONE, TEAM_EMAIL_DOMAIN } from "@dream/config/constants";
import {
  Button,
  ErrorState,
  IconBolt,
  IconCalendar,
  IconClients,
  IconClock,
  IconGoogle,
  IconLogout,
  IconMenu,
  IconMoon,
  IconPublish,
  IconSettings,
  IconShield,
  IconSun,
  IconUserPlus,
  IconVideo,
  Input,
  MenuSelect,
  SegmentedControl,
  Skeleton,
  Toggle,
  useToast,
} from "@dream/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { type ReactNode, useEffect } from "react";
import { ApiError, api, unwrap } from "../api/client.ts";
import { ColorSwatches } from "../components/color-swatches.tsx";
import { useAdminCatalog } from "../hooks/use-calendar.ts";
import { useAdminChrome } from "../layouts/admin-chrome.tsx";
import { inviteEmailSummary } from "../lib/client-emails.ts";
import { formatRelativePt } from "../lib/format-relative.ts";
import { displayNameFromParts, initials } from "../lib/people.ts";
import {
  type CalendarPreferences,
  type DayHours,
  syncSlotBounds,
  timeOptions,
  WEEKDAY_LABELS,
  type Weekday,
} from "../preferences/preferences.ts";
import { usePreferences } from "../preferences/provider.tsx";
import { calendarSearch } from "../router.tsx";
import { useTheme } from "../theme/theme.tsx";

export type SettingsSection =
  | "geral"
  | "barra"
  | "cores"
  | "calendarios"
  | "contatos"
  | "publicacoes"
  | "horarios"
  | "flexivel"
  | "foco"
  | "portal"
  | "perfil";

const SETTINGS_SECTION_ALIASES: Record<string, SettingsSection> = {
  coloracao: "cores",
  integracoes: "publicacoes",
  links: "portal",
  flexiveis: "flexivel",
};

export function parseSettingsSection(raw: unknown): SettingsSection {
  if (typeof raw !== "string" || raw.trim() === "") return "geral";
  const key = raw.trim().toLowerCase();
  if (SETTINGS_SECTION_IDS.includes(key as SettingsSection)) return key as SettingsSection;
  return SETTINGS_SECTION_ALIASES[key] ?? "geral";
}

const NAV: Array<{
  group: string;
  items: Array<{ id: SettingsSection; label: string; icon: ReactNode }>;
}> = [
  {
    group: "Meu calendário",
    items: [
      { id: "geral", label: "Geral", icon: <IconSettings className="size-4" /> },
      { id: "barra", label: "Barra do calendário", icon: <IconMenu className="size-4" /> },
      {
        id: "cores",
        label: "Cores dos tipos",
        icon: <IconCalendar className="size-4" />,
      },
    ],
  },
  {
    group: "Integração",
    items: [
      { id: "calendarios", label: "Calendários", icon: <IconCalendar className="size-4" /> },
      { id: "contatos", label: "Contatos", icon: <IconClients className="size-4" /> },
      { id: "publicacoes", label: "Publicações", icon: <IconVideo className="size-4" /> },
    ],
  },
  {
    group: "Agendamento",
    items: [
      { id: "horarios", label: "Horários de trabalho", icon: <IconClock className="size-4" /> },
      { id: "flexivel", label: "Entregas flexíveis", icon: <IconBolt className="size-4" /> },
      { id: "foco", label: "Proteção de foco", icon: <IconShield className="size-4" /> },
      { id: "portal", label: "Portal do cliente", icon: <IconClients className="size-4" /> },
    ],
  },
  {
    group: "Conta",
    items: [{ id: "perfil", label: "Perfil", icon: <IconUserPlus className="size-4" /> }],
  },
];

export const SETTINGS_SECTION_IDS: SettingsSection[] = NAV.flatMap((group) =>
  group.items.map((item) => item.id),
);

export function SettingsPage({ section }: { section: SettingsSection }) {
  const navigate = useNavigate();
  const chrome = useAdminChrome();

  function go(next: SettingsSection): void {
    void navigate({
      to: "/configuracoes",
      search: next === "geral" ? {} : { secao: next },
    });
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden bg-[var(--color-surface)]">
      <aside className="dream-scroll hidden w-[240px] shrink-0 flex-col overflow-y-auto border-r border-[var(--color-line)] bg-[var(--color-surface-subtle)] p-3 md:flex">
        <button
          type="button"
          onClick={() => void navigate({ to: "/calendario", search: calendarSearch() })}
          className="mb-3 rounded-md px-2.5 py-1 text-left text-[13px] font-semibold text-[var(--color-ink-muted)] transition-[color,background-color] duration-[var(--dream-duration-fast)] hover:bg-[var(--color-surface)] hover:text-[var(--color-ink)]"
        >
          ← Voltar
        </button>
        {NAV.map((group) => (
          <div key={group.group} className="mb-3">
            <p className="px-2.5 pb-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--color-ink-muted)]">
              {group.group}
            </p>
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const active = item.id === section;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => go(item.id)}
                      className={[
                        "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[14px]",
                        "transition-[color,background-color,transform] duration-[var(--dream-duration-fast)] ease-[var(--dream-ease-standard)]",
                        active
                          ? "bg-[#D0E5FF] font-semibold text-[#0074FF]"
                          : "font-medium text-[var(--color-ink-secondary)] hover:bg-[var(--color-surface)] hover:text-[var(--color-ink)]",
                      ].join(" ")}
                    >
                      {item.icon}
                      {item.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        <button
          type="button"
          onClick={() => void chrome.signOut()}
          className="mt-auto flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[14px] font-semibold text-[#AB3434] hover:bg-[#FEEDED]"
        >
          <IconLogout className="size-4" />
          Sair
        </button>
      </aside>

      <div className="dream-scroll min-w-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl px-4 py-6 sm:px-8">
          <div className="mb-4 flex items-center justify-between gap-3 md:hidden">
            <button
              type="button"
              onClick={() => void navigate({ to: "/calendario", search: calendarSearch() })}
              className="rounded-md px-2 py-1 text-[13px] font-semibold text-[var(--color-ink-muted)] transition-colors duration-[var(--dream-duration-fast)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-ink)]"
            >
              ← Voltar
            </button>
            <select
              aria-label="Seção das configurações"
              value={section}
              onChange={(event) => go(event.target.value as SettingsSection)}
              className="h-9 rounded-lg border border-[var(--color-line)] bg-[var(--color-surface)] px-2 text-[13px]"
            >
              {NAV.flatMap((group) => group.items).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
          <h1 className="mb-6 text-[24px] font-bold tracking-[0] text-[var(--color-ink)]">
            Configurações
          </h1>
          {section === "geral" && <GeneralSection />}
          {section === "barra" && <MenubarSection />}
          {section === "cores" && <ColorSection />}
          {section === "calendarios" && <CalendarsSection />}
          {section === "contatos" && <ContactsSection />}
          {section === "publicacoes" && <IntegrationsSection />}
          {section === "horarios" && <HoursSection />}
          {section === "flexivel" && <FlexibleSection />}
          {section === "foco" && <FocusSection />}
          {section === "portal" && <LinksSection />}
          {section === "perfil" && <ProfileSection />}
        </div>
      </div>
    </div>
  );
}

function SettingsRow({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 border-b border-[var(--color-line)] py-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 sm:pr-6">
        <p className="text-[14px] font-bold text-[var(--color-ink)]">{title}</p>
        <p className="mt-0.5 text-[13px] leading-5 text-[var(--color-ink-muted)]">{description}</p>
      </div>
      <div className="shrink-0 sm:max-w-[18rem]">{children}</div>
    </div>
  );
}

function HowItWorks({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="dream-mini-window mb-5 p-4">
      <p className="flex items-center gap-2 text-[14px] font-bold text-[var(--color-ink)]">
        <IconBolt className="size-4 text-[#0074FF]" />
        {title}
      </p>
      <p className="mt-1.5 text-[13px] leading-5 text-[var(--color-ink-muted)]">{children}</p>
    </div>
  );
}

function GeneralSection() {
  const { prefs, setPrefs } = usePreferences();
  const { theme, setTheme } = useTheme();

  return (
    <div>
      <SettingsRow
        title="Início da semana"
        description="Define a primeira coluna da grade e do mini mês."
      >
        <SegmentedControl
          aria-label="Início da semana"
          value={prefs.weekStartsOn === 0 ? "sun" : "mon"}
          onChange={(next) => setPrefs({ weekStartsOn: next === "sun" ? 0 : 1 })}
          options={[
            { value: "mon", label: "Segunda" },
            { value: "sun", label: "Domingo" },
          ]}
        />
      </SettingsRow>
      <SettingsRow
        title="Formato de hora"
        description="Como os horários aparecem na grade semanal e diária."
      >
        <SegmentedControl
          aria-label="Formato de hora"
          value={prefs.timeFormat}
          onChange={(next) => setPrefs({ timeFormat: next })}
          options={[
            { value: "24", label: "24 horas" },
            { value: "12", label: "12 horas" },
          ]}
        />
      </SettingsRow>
      <SettingsRow
        title="Mostrar fins de semana"
        description="Esconde sábado e domingo na grade semanal e mensal."
      >
        <Toggle
          checked={prefs.showWeekends}
          onCheckedChange={(checked) => setPrefs({ showWeekends: checked })}
          aria-label="Mostrar fins de semana"
        />
      </SettingsRow>
      <SettingsRow title="Densidade" description="Compacto encolhe os chips e os slots da grade.">
        <SegmentedControl
          aria-label="Densidade"
          value={prefs.density}
          onChange={(next) => setPrefs({ density: next })}
          options={[
            { value: "comfortable", label: "Confortável" },
            { value: "compact", label: "Compacto" },
          ]}
        />
      </SettingsRow>
      <SettingsRow
        title="Fuso operacional"
        description="As entregas são gravadas neste fuso. Não há segundo fuso de exibição."
      >
        <p className="text-right text-[13px] font-semibold text-[var(--color-ink)]">
          {DEFAULT_TIMEZONE}
        </p>
      </SettingsRow>
      <SettingsRow
        title="Duração padrão"
        description="Usada quando você clica num horário vazio para criar uma entrega."
      >
        <MenuSelect
          aria-label="Duração padrão"
          value={String(prefs.defaultDurationMin)}
          onChange={(next) => setPrefs({ defaultDurationMin: Number(next) as 15 | 30 | 45 | 60 })}
          options={[
            { value: "15", label: "15 min" },
            { value: "30", label: "30 min" },
            { value: "45", label: "45 min" },
            { value: "60", label: "60 min" },
          ]}
        />
      </SettingsRow>
      <SettingsRow
        title="Horas visíveis"
        description="Quantas horas a grade semanal mostra a partir do início do expediente."
      >
        <MenuSelect
          aria-label="Horas visíveis"
          value={String(prefs.visibleHours)}
          onChange={(next) => {
            const hours = Number(next) as 8 | 12 | 16;
            const [h, m] = prefs.slotMin.split(":").map(Number);
            const start = (h ?? 7) * 60 + (m ?? 0);
            const end = Math.min(start + hours * 60, 24 * 60);
            const eh = String(Math.floor(end / 60)).padStart(2, "0");
            const em = String(end % 60).padStart(2, "0");
            setPrefs({ visibleHours: hours, slotMax: `${eh}:${em}` });
          }}
          options={[
            { value: "8", label: "8 horas" },
            { value: "12", label: "12 horas" },
            { value: "16", label: "16 horas" },
          ]}
        />
      </SettingsRow>
      <SettingsRow
        title="Tema"
        description="Claro é o padrão operacional. Escuro é uma escolha explícita."
      >
        <SegmentedControl
          aria-label="Tema"
          value={theme}
          onChange={(next) => setTheme(next)}
          options={[
            { value: "light", label: "Claro", icon: <IconSun className="size-3.5" /> },
            { value: "dark", label: "Escuro", icon: <IconMoon className="size-3.5" /> },
          ]}
        />
      </SettingsRow>
      <SettingsRow
        title="Cursor em ponteiro"
        description="Botões e links usam o ponteiro da mão, como no kit."
      >
        <Toggle
          checked={prefs.pointerCursor}
          onCheckedChange={(checked) => setPrefs({ pointerCursor: checked })}
          aria-label="Cursor em ponteiro"
        />
      </SettingsRow>
      <SettingsRow
        title="Reduzir movimento"
        description="Desliga as animações curtas do chrome, além da preferência do sistema."
      >
        <Toggle
          checked={prefs.reduceMotion}
          onCheckedChange={(checked) => setPrefs({ reduceMotion: checked })}
          aria-label="Reduzir movimento"
        />
      </SettingsRow>
    </div>
  );
}

function MenubarSection() {
  const { prefs, setPrefs } = usePreferences();
  return (
    <div>
      <SettingsRow
        title="Mostrar próxima entrega"
        description="A pill flutuante no fundo da grade, equivalente à barra de próximos eventos do kit."
      >
        <Toggle
          checked={prefs.menubarUpcoming}
          onCheckedChange={(checked) => setPrefs({ menubarUpcoming: checked })}
          aria-label="Mostrar próxima entrega"
        />
      </SettingsRow>
      <SettingsRow
        title="Eventos a incluir"
        description="A pill considera só o dia de hoje ou a semana à frente."
      >
        <MenuSelect
          aria-label="Eventos a incluir"
          value={prefs.upcomingHorizon}
          onChange={(next) => setPrefs({ upcomingHorizon: next as "today" | "week" })}
          options={[
            { value: "today", label: "Hoje" },
            { value: "week", label: "Esta semana" },
          ]}
        />
      </SettingsRow>
      <SettingsRow
        title="Vista padrão"
        description="Abre nesta vista quando você entra no calendário sem um link específico."
      >
        <MenuSelect
          aria-label="Vista padrão"
          value={prefs.defaultView}
          onChange={(next) => setPrefs({ defaultView: next as CalendarView })}
          options={[
            { value: "mes", label: "Mês" },
            { value: "semana", label: "Semana" },
            { value: "dia", label: "Dia" },
            { value: "agenda", label: "Agenda" },
            { value: "linha", label: "Linha" },
          ]}
        />
      </SettingsRow>
      <SettingsRow title="Busca na barra" description="Ícone de busca (⌘K) à direita da toolbar.">
        <Toggle
          checked={prefs.showSearchButton}
          onCheckedChange={(checked) => setPrefs({ showSearchButton: checked })}
          aria-label="Mostrar busca na barra"
        />
      </SettingsRow>
      <SettingsRow title="Atalhos" description="O raio abre a paleta de comando, como no kit.">
        <Toggle
          checked={prefs.showShortcutsButton}
          onCheckedChange={(checked) => setPrefs({ showShortcutsButton: checked })}
          aria-label="Mostrar atalhos na barra"
        />
      </SettingsRow>
      <SettingsRow
        title="Disponibilidade"
        description="Botão que liga e desliga o recorte do expediente na grade."
      >
        <Toggle
          checked={prefs.showAvailabilityButton}
          onCheckedChange={(checked) => setPrefs({ showAvailabilityButton: checked })}
          aria-label="Mostrar disponibilidade na barra"
        />
      </SettingsRow>
      <SettingsRow title="Nova entrega" description="Botão primário da toolbar.">
        <Toggle
          checked={prefs.showCreateButton}
          onCheckedChange={(checked) => setPrefs({ showCreateButton: checked })}
          aria-label="Mostrar nova entrega na barra"
        />
      </SettingsRow>
    </div>
  );
}

function ColorSection() {
  const { prefs, setPrefs } = usePreferences();
  const catalog = useAdminCatalog();

  function patchType(typeId: string, color: string): void {
    setPrefs({
      typeColorOverrides: { ...prefs.typeColorOverrides, [typeId]: color },
    });
  }

  function toggleType(typeId: string, enabled: boolean): void {
    const muted = new Set(prefs.mutedTypeIds);
    if (enabled) muted.delete(typeId);
    else muted.add(typeId);
    setPrefs({ mutedTypeIds: [...muted] });
  }

  return (
    <div>
      <SettingsRow
        title="Colorir entregas"
        description="Por tipo (post, reels, reunião…) ou pela cor cadastrada do cliente."
      >
        <SegmentedControl
          aria-label="Colorir por"
          value={prefs.colorBy}
          onChange={(next) => setPrefs({ colorBy: next })}
          options={[
            { value: "type", label: "Tipo" },
            { value: "client", label: "Cliente" },
          ]}
        />
      </SettingsRow>

      <p className="mb-3 mt-6 text-[13px] leading-5 text-[var(--color-ink-muted)]">
        Cada tipo ganha a paleta pastel do kit. Desligar o tipo deixa o chip cinza.
      </p>

      <ul className="divide-y divide-[var(--color-line)]">
        {catalog.types.map((type) => {
          const enabled = !prefs.mutedTypeIds.includes(type.id);
          const color = prefs.typeColorOverrides[type.id] ?? type.color;
          return (
            <li key={type.id} className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center">
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 text-[14px] font-bold text-[var(--color-ink)]">
                  <span
                    className="size-3.5 rounded-[4px]"
                    style={{ backgroundColor: color }}
                    aria-hidden="true"
                  />
                  {type.label}
                </p>
              </div>
              <ColorSwatches
                value={color}
                onChange={(next) => patchType(type.id, next)}
                ariaLabel={`Cor de ${type.label}`}
              />
              <Toggle
                checked={enabled}
                onCheckedChange={(checked) => toggleType(type.id, checked)}
                aria-label={`Colorir ${type.label}`}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
}

interface ProviderCard {
  id: string;
  name: string;
  color: string;
  category: string;
}

interface AccountCard {
  platform: string;
  status: string;
  live: boolean;
  displayName: string;
}

function googleAccessRoleLabel(role: string): string {
  if (role === "owner") return "Dono";
  if (role === "writer") return "Pode editar";
  if (role === "reader") return "Somente leitura";
  if (role === "freeBusyReader") return "Ocupado/livre";
  return role;
}

function ScheduleInvitesSection() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const me = useQuery({
    queryKey: ["me"],
    queryFn: async () =>
      unwrap(await api.api.me.get()) as {
        email: string;
        scheduleInviteClient?: boolean;
        scheduleInviteTeam?: boolean;
      },
  });
  const inviteClient = me.data?.scheduleInviteClient ?? true;
  const inviteTeam = me.data?.scheduleInviteTeam ?? false;

  const save = useMutation({
    mutationFn: async (patch: { inviteClient?: boolean; inviteTeam?: boolean }) =>
      unwrap(await api.api.admin["schedule-invites"].patch(patch)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      toast.success("Padrão de convite atualizado");
    },
    onError: (error: unknown) =>
      toast.error(
        "Não foi possível salvar",
        error instanceof ApiError ? error.message : "Tente novamente.",
      ),
  });

  return (
    <div className="mb-6">
      <SettingsRow
        title="Convidar o e-mail do cliente"
        description="Novas entregas mandam o convite da Google Agenda para o e-mail cadastrado na ficha do cliente. Dá para mudar em cada tarefa."
      >
        <Toggle
          checked={inviteClient}
          onCheckedChange={(checked) => save.mutate({ inviteClient: checked })}
          disabled={save.isPending || me.isLoading}
          aria-label="Convidar o e-mail do cliente nas entregas novas"
        />
      </SettingsRow>
      <SettingsRow
        title="Convidar a equipe inteira"
        description={`Manda o mesmo convite para todos os e-mails @${TEAM_EMAIL_DOMAIN}, não só para o responsável.`}
      >
        <Toggle
          checked={inviteTeam}
          onCheckedChange={(checked) => save.mutate({ inviteTeam: checked })}
          disabled={save.isPending || me.isLoading}
          aria-label="Convidar a equipe inteira nas entregas novas"
        />
      </SettingsRow>
    </div>
  );
}

function CalendarsSection() {
  const catalog = useAdminCatalog();
  const toast = useToast();
  const queryClient = useQueryClient();

  const status = useQuery({
    queryKey: ["google-calendar"],
    queryFn: async () => unwrap(await api.api.admin["google-calendar"].get()),
  });

  useEffect(() => {
    const flag = new URLSearchParams(window.location.search).get("google");
    if (flag === "ok") {
      toast.success("Google Agenda conectada. Entregas novas já entram na sua Agenda.");
    }
    if (flag === "denied") toast.error("Você recusou o acesso à Agenda.");
    if (flag === "error" || flag === "state" || flag === "missing") {
      toast.error("Não foi possível conectar a Agenda. Confira os redirects no Google Cloud.");
    }
  }, [toast]);

  const connect = useMutation({
    mutationFn: async () => unwrap(await api.api.admin["google-calendar"].connect.post()),
    onSuccess: (data) => {
      if ("url" in data && typeof data.url === "string") window.location.assign(data.url);
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : "Não foi possível iniciar o Google.");
    },
  });

  const syncNow = useMutation({
    mutationFn: async () => unwrap(await api.api.admin["google-calendar"].sync.post()),
    onSuccess: () => {
      toast.success("Agenda sincronizada.");
      void queryClient.invalidateQueries({ queryKey: ["google-calendar"] });
      void queryClient.invalidateQueries({ queryKey: ["google-calendar-events"] });
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : "Falha ao sincronizar.");
    },
  });

  const disconnect = useMutation({
    mutationFn: async () => unwrap(await api.api.admin["google-calendar"].delete()),
    onSuccess: () => {
      toast.success("Google Agenda desconectada.");
      void queryClient.invalidateQueries({ queryKey: ["google-calendar"] });
      void queryClient.invalidateQueries({ queryKey: ["google-calendar-events"] });
    },
  });

  const toggle = useMutation({
    mutationFn: async (input: { id: string; enabled: boolean }) =>
      unwrap(
        await api.api.admin["google-calendar"].calendars({ id: input.id }).patch({
          enabled: input.enabled,
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["google-calendar"] });
      void queryClient.invalidateQueries({ queryKey: ["google-calendar-events"] });
    },
  });

  const connected = status.data?.connected === true;
  const needsReconnect =
    connected && status.data !== undefined && "needsReconnect" in status.data
      ? status.data.needsReconnect === true
      : false;
  const canWrite =
    connected && status.data !== undefined && "canWrite" in status.data
      ? status.data.canWrite === true
      : false;

  return (
    <div>
      <HowItWorks title="Como a Agenda entra aqui">
        {`O login com @${TEAM_EMAIL_DOMAIN} já pede o acesso completo da Agenda (ver, criar, editar e apagar). Toda entrega criada no Dream Calendar vai para a agenda principal. O convite pode ir ao e-mail do cliente, a toda a equipe @${TEAM_EMAIL_DOMAIN}, ou só ao responsável. Reuniões que já estavam no Google aparecem como overlay — Meet, local e convidados no hover. O cliente não vê o overlay. A sync do worker avisa o calendário em tempo real pelo WebSocket.`}
      </HowItWorks>

      <ScheduleInvitesSection />

      <div className="mb-6 rounded-xl border border-[var(--color-line)] p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="flex items-center gap-2 text-[14px] font-bold text-[var(--color-ink)]">
              <IconGoogle className="size-4" />
              Google Agenda
            </p>
            {connected ? (
              <p className="mt-1 text-[13px] text-[var(--color-ink-muted)]">
                Conectada como {status.data && "email" in status.data ? status.data.email : ""}.{" "}
                {canWrite && !needsReconnect
                  ? "Entregas novas e alterações já vão para esta Agenda, com o seu e-mail no evento."
                  : needsReconnect && canWrite
                    ? "A autorização caiu. Reconecte — o Google pede o acesso completo de novo, uma vez."
                    : "Esta conexão só lê a Agenda. Reconecte uma vez para as entregas irem para o Google."}{" "}
                {status.data &&
                "lastSyncedAt" in status.data &&
                status.data.lastSyncedAt !== null &&
                status.data.lastSyncedAt !== undefined
                  ? `Última sync ${formatRelativePt(status.data.lastSyncedAt)}.`
                  : "Ainda não sincronizou."}
              </p>
            ) : (
              <p className="mt-1 text-[13px] text-[var(--color-ink-muted)]">
                O login com @{TEAM_EMAIL_DOMAIN} já pede a Agenda completa. Se ainda não aparecer
                aqui, conecte agora: ver, criar, editar e apagar eventos. Cada entrega do calendário
                entra na agenda principal com o seu e-mail.
              </p>
            )}
            {needsReconnect && (
              <p className="mt-2 text-[13px] font-medium text-[#AB3434]">
                {canWrite
                  ? "O Google recusou o token atual. Clique em Reconectar e autorize a Agenda de novo."
                  : "A autorização antiga é só de leitura. Clique em Reconectar e permita o acesso completo — uma vez só."}
              </p>
            )}
            {connected &&
              !needsReconnect &&
              status.data &&
              "lastError" in status.data &&
              status.data.lastError !== null && (
                <p className="mt-1 text-[13px] font-medium text-[#AB3434]">
                  {status.data.lastError}
                </p>
              )}
            {status.data?.googleReady === false && (
              <p className="mt-2 text-[13px] font-medium text-[#AB3434]">
                GOOGLE_CLIENT_ID não está configurado neste ambiente. O botão fica desligado até
                colar as chaves no .env.
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {connected ? (
              <>
                {needsReconnect ? (
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    loading={connect.isPending}
                    disabled={status.data?.googleReady === false}
                    onClick={() => connect.mutate()}
                  >
                    Reconectar
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  loading={syncNow.isPending}
                  onClick={() => syncNow.mutate()}
                >
                  Sincronizar agora
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  loading={disconnect.isPending}
                  onClick={() => disconnect.mutate()}
                >
                  Desconectar
                </Button>
              </>
            ) : (
              <Button
                type="button"
                variant="primary"
                size="sm"
                loading={connect.isPending}
                disabled={status.data?.googleReady === false}
                onClick={() => connect.mutate()}
              >
                Conectar Google Agenda
              </Button>
            )}
          </div>
        </div>
        {status.isLoading ? (
          <ul className="mt-4 space-y-2 rounded-lg border border-[var(--color-line)] p-3">
            {[0, 1, 2].map((row) => (
              <li key={row} className="flex items-center gap-3">
                <Skeleton className="size-2.5 rounded-full" />
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-5 w-10" />
              </li>
            ))}
          </ul>
        ) : status.error !== null ? (
          <div className="mt-4">
            <ErrorState
              message={
                status.error instanceof ApiError
                  ? status.error.message
                  : "Não foi possível carregar a Agenda."
              }
              onRetry={() => void status.refetch()}
            />
          </div>
        ) : connected &&
          status.data &&
          "calendars" in status.data &&
          status.data.calendars.length === 0 ? (
          <p className="mt-4 rounded-lg border border-dashed border-[var(--color-line)] px-3 py-4 text-[13px] text-[var(--color-ink-muted)]">
            Nenhuma agenda listada ainda. Clique em Sincronizar agora.
          </p>
        ) : connected && status.data && "calendars" in status.data ? (
          <ul className="mt-4 divide-y divide-[var(--color-line)] rounded-lg border border-[var(--color-line)]">
            {status.data.calendars.map((calendar) => (
              <li key={calendar.id} className="flex items-center gap-3 px-3 py-2.5">
                <span
                  className="size-2.5 shrink-0 rounded-full ring-2 ring-[var(--color-surface)]"
                  style={{ backgroundColor: calendar.backgroundColor ?? "#039BE5" }}
                  aria-hidden="true"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[14px] font-semibold text-[var(--color-ink)]">
                    {calendar.summary}
                    {calendar.isPrimary ? (
                      <span className="ml-2 text-[11px] font-medium text-[var(--color-ink-muted)]">
                        principal
                      </span>
                    ) : null}
                  </p>
                  {"accessRole" in calendar &&
                  typeof calendar.accessRole === "string" &&
                  calendar.accessRole !== "" ? (
                    <p className="truncate text-[11px] text-[var(--color-ink-muted)]">
                      {googleAccessRoleLabel(calendar.accessRole)}
                    </p>
                  ) : null}
                </div>
                <Toggle
                  checked={calendar.enabled}
                  onCheckedChange={(enabled) => toggle.mutate({ id: calendar.id, enabled })}
                  aria-label={`Sincronizar ${calendar.summary}`}
                />
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <p className="mb-3 text-[14px] font-bold text-[var(--color-ink)]">Calendários de cliente</p>
      <ul className="mb-6 divide-y divide-[var(--color-line)] rounded-xl border border-[var(--color-line)]">
        {catalog.clients.map((client) => (
          <li key={client.id} className="flex items-center gap-3 px-4 py-3">
            <span
              className="size-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: client.color }}
              aria-hidden="true"
            />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[14px] font-semibold text-[var(--color-ink)]">
                {client.name}
              </p>
              <p className="truncate text-[12px] text-[var(--color-ink-muted)]">
                {inviteEmailSummary(client)}
              </p>
            </div>
            <Link
              to="/calendario"
              search={calendarSearch({ clients: client.id })}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-[var(--color-line)] bg-[var(--color-surface)] px-2.5 text-[12px] font-semibold shadow-[var(--shadow-xs)]"
            >
              <IconClock className="size-3.5" />
              Reagendar
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ContactsSection() {
  const catalog = useAdminCatalog();
  return (
    <div>
      <HowItWorks title="Salas e contatos">
        Não há salas neste operacional. Contatos são as pessoas da equipe e os e-mails de cada
        cliente, usados no convite da Google Agenda quando a entrega é lançada.
      </HowItWorks>
      <p className="mb-2 text-[13px] font-bold text-[var(--color-ink)]">Equipe</p>
      <ul className="mb-6 divide-y divide-[var(--color-line)] rounded-xl border border-[var(--color-line)]">
        {catalog.team.length === 0 ? (
          <li className="px-4 py-6 text-[13px] text-[var(--color-ink-muted)]">
            Nenhuma pessoa na equipe ainda.
          </li>
        ) : (
          catalog.team.map((member) => (
            <li key={member.id} className="flex items-center gap-3 px-4 py-3">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-[#D0E5FF] text-[12px] font-bold text-[#0074FF]">
                {initials(member.name)}
              </span>
              <div className="min-w-0">
                <p className="truncate text-[14px] font-semibold text-[var(--color-ink)]">
                  {member.name}
                </p>
                <p className="truncate text-[12px] text-[var(--color-ink-muted)]">{member.email}</p>
              </div>
            </li>
          ))
        )}
      </ul>
      <p className="mb-2 text-[13px] font-bold text-[var(--color-ink)]">Clientes</p>
      <ul className="divide-y divide-[var(--color-line)] rounded-xl border border-[var(--color-line)]">
        {catalog.clients.length === 0 ? (
          <li className="px-4 py-6 text-[13px] text-[var(--color-ink-muted)]">
            Nenhum cliente cadastrado.
          </li>
        ) : (
          catalog.clients.map((client) => (
            <li key={client.id} className="flex items-center gap-3 px-4 py-3">
              <span
                className="size-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: client.color }}
                aria-hidden="true"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[14px] font-semibold text-[var(--color-ink)]">
                  {client.name}
                </p>
                <p className="truncate text-[12px] text-[var(--color-ink-muted)]">
                  {inviteEmailSummary(client)}
                </p>
              </div>
              <Link
                to="/clientes"
                className="text-[12px] font-semibold text-[#0074FF] hover:underline"
              >
                Editar
              </Link>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}

function IntegrationsSection() {
  const { prefs, setPrefs } = usePreferences();
  return (
    <div>
      <SettingsRow
        title="Videochamada padrão"
        description="Nova reunião pode nascer com um link customizado. Não há Google Meet nem Zoom embutidos."
      >
        <MenuSelect
          aria-label="Videochamada padrão"
          value={prefs.defaultVideo}
          onChange={(next) => setPrefs({ defaultVideo: next as "none" | "custom" })}
          options={[
            { value: "none", label: "Sem videochamada" },
            { value: "custom", label: "Link customizado" },
          ]}
        />
      </SettingsRow>
      {prefs.defaultVideo === "custom" && (
        <SettingsRow
          title="Link customizado"
          description="Gravado neste navegador e sugerido no drawer da entrega."
        >
          <Input
            aria-label="Link customizado de videochamada"
            placeholder="https://…"
            value={prefs.customMeetUrl}
            onChange={(event) => setPrefs({ customMeetUrl: event.target.value })}
          />
        </SettingsRow>
      )}
      <SettingsRow
        title="Entrar automaticamente"
        description="O kit liga isso no app da barra. Aqui a pill da próxima entrega é o atalho — não abre reunião sozinha."
      >
        <Toggle
          checked={prefs.autoJoinMeetings}
          onCheckedChange={(checked) => setPrefs({ autoJoinMeetings: checked })}
          aria-label="Entrar automaticamente"
        />
      </SettingsRow>
      <p className="mb-3 mt-6 text-[14px] font-bold text-[var(--color-ink)]">Provedores</p>
      <div className="mb-4">
        <Link
          to="/publicacoes"
          className="inline-flex h-10 items-center gap-2 rounded-lg bg-[#0074FF] px-4 text-[14px] font-semibold text-white"
        >
          <IconPublish className="size-4" />
          Abrir publicações
        </Link>
      </div>
      <ProviderCards />
    </div>
  );
}

function ProviderCards() {
  const catalog = useAdminCatalog();
  const clientId = catalog.clients[0]?.id ?? "";

  const providers = useQuery({
    queryKey: ["social-providers"],
    queryFn: async () => unwrap(await api.api.admin.social.providers.get()) as ProviderCard[],
  });
  const accounts = useQuery({
    queryKey: ["social-accounts", clientId],
    enabled: clientId !== "",
    queryFn: async () =>
      unwrap(await api.api.admin.social.accounts.get({ query: { clientId } })) as AccountCard[],
  });

  const byPlatform = new Map(
    (accounts.data ?? []).filter((a) => a.status === "active").map((a) => [a.platform, a]),
  );

  const featured = (providers.data ?? []).slice(0, 3);
  const rest = (providers.data ?? []).slice(3, 12);

  return (
    <>
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        {featured.map((provider) => {
          const account = byPlatform.get(provider.id);
          return (
            <div key={provider.id} className="dream-mini-window flex flex-col gap-3 p-4">
              <span
                className="size-8 rounded-lg"
                style={{ backgroundColor: provider.color }}
                aria-hidden="true"
              />
              <p className="text-[14px] font-bold text-[var(--color-ink)]">{provider.name}</p>
              <Link
                to="/publicacoes"
                className="inline-flex h-9 items-center justify-center rounded-lg bg-[#0074FF] px-3 text-[13px] font-semibold text-white"
              >
                {account === undefined ? "Conectar" : "Gerenciar"}
              </Link>
            </div>
          );
        })}
      </div>
      <ul className="divide-y divide-[var(--color-line)] rounded-xl border border-[var(--color-line)]">
        {rest.map((provider) => {
          const account = byPlatform.get(provider.id);
          return (
            <li key={provider.id} className="flex items-center gap-3 px-4 py-3">
              <span
                className="size-8 shrink-0 rounded-lg"
                style={{ backgroundColor: provider.color }}
                aria-hidden="true"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[14px] font-semibold text-[var(--color-ink)]">
                  {provider.name}
                </p>
                <p className="truncate text-[12px] text-[var(--color-ink-muted)]">
                  {account === undefined
                    ? "Desconectada"
                    : account.live
                      ? `${account.displayName} · ao vivo`
                      : `${account.displayName} · conectada`}
                </p>
              </div>
              <Link
                to="/publicacoes"
                className="shrink-0 text-[13px] font-semibold text-[#0074FF] hover:underline"
              >
                {account === undefined ? "Conectar" : "Gerenciar"}
              </Link>
            </li>
          );
        })}
      </ul>
    </>
  );
}

function HoursSection() {
  const { prefs, setPrefs } = usePreferences();
  const times = timeOptions();

  function patchDay(day: Weekday, patch: Partial<DayHours>): void {
    const workingDays = prefs.workingDays.map((item) =>
      item.day === day ? { ...item, ...patch, day } : item,
    );
    setPrefs({ workingDays, ...syncSlotBounds(workingDays) });
  }

  return (
    <div>
      <SettingsRow
        title="Respeitar horário de trabalho"
        description="A grade recorta o dia fora deste intervalo. Desligue para ver 24 horas."
      >
        <Toggle
          checked={prefs.workingHoursEnabled}
          onCheckedChange={(checked) => setPrefs({ workingHoursEnabled: checked })}
          aria-label="Respeitar horário de trabalho"
        />
      </SettingsRow>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {prefs.workingDays.map((day) => (
          <div key={day.day} className="dream-mini-window p-3">
            <div className="mb-3 flex items-center justify-between gap-2">
              <p className="flex items-center gap-1.5 text-[13px] font-bold text-[var(--color-ink)]">
                <IconClock className="size-3.5 text-[var(--color-ink-muted)]" />
                Data e hora
              </p>
              <div className="flex items-center gap-2 text-[12px] font-semibold text-[#0074FF]">
                Disponível
                <Toggle
                  checked={day.enabled}
                  onCheckedChange={(checked) => patchDay(day.day, { enabled: checked })}
                  aria-label={`Disponível ${WEEKDAY_LABELS[day.day]}`}
                />
              </div>
            </div>
            <div className="grid gap-2">
              <MenuSelect
                aria-label={`Dia ${WEEKDAY_LABELS[day.day]}`}
                value={String(day.day)}
                onChange={() => undefined}
                options={[{ value: String(day.day), label: WEEKDAY_LABELS[day.day] }]}
              />
              <div className="grid grid-cols-2 gap-2">
                <MenuSelect
                  aria-label={`Início ${WEEKDAY_LABELS[day.day]}`}
                  value={day.from}
                  onChange={(next) => patchDay(day.day, { from: next })}
                  options={times}
                />
                <MenuSelect
                  aria-label={`Fim ${WEEKDAY_LABELS[day.day]}`}
                  value={day.to}
                  onChange={(next) => patchDay(day.day, { to: next })}
                  options={times}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 border-t border-[var(--color-line)] pt-4">
        <SettingsRow
          title="Almoço"
          description="Fura o expediente no meio do dia e marca a faixa como indisponível."
        >
          <Toggle
            checked={prefs.lunchEnabled}
            onCheckedChange={(checked) => setPrefs({ lunchEnabled: checked })}
            aria-label="Reservar almoço"
          />
        </SettingsRow>
        {prefs.lunchEnabled && (
          <div className="flex flex-wrap items-center gap-2 py-3 text-[14px] text-[var(--color-ink)]">
            <span>Reservo</span>
            <MenuSelect
              aria-label="Duração do almoço"
              value={String(prefs.lunchMinutes)}
              onChange={(next) => setPrefs({ lunchMinutes: Number(next) as 15 | 30 | 45 | 60 })}
              options={[
                { value: "15", label: "15 min" },
                { value: "30", label: "30 min" },
                { value: "45", label: "45 min" },
                { value: "60", label: "60 min" },
              ]}
            />
            <span>às</span>
            <MenuSelect
              aria-label="Horário do almoço"
              value={prefs.lunchAt}
              onChange={(next) => setPrefs({ lunchAt: next })}
              options={times}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function FlexibleSection() {
  const { prefs, setPrefs } = usePreferences();
  return (
    <div>
      <HowItWorks title="Como funciona?">
        No kit, eventos flexíveis se rearranjam sozinhos. Aqui isso vira arraste na grade: a equipe
        empurra a entrega para o próximo buraco livre. O convite da Agenda (cliente e/ou equipe)
        segue o padrão em Calendários.
      </HowItWorks>
      <SettingsRow
        title="Flexível por padrão"
        description="Você ainda pode travar o arraste abaixo, por sessão neste navegador."
      >
        <Toggle
          checked={prefs.flexibleByDefault}
          onCheckedChange={(checked) =>
            setPrefs({ flexibleByDefault: checked, allowDrag: checked })
          }
          aria-label="Flexível por padrão"
        />
      </SettingsRow>
      <SettingsRow
        title="Permitir arrastar entregas"
        description="Desligue para a grade ficar só de leitura no gesto — o drawer continua editável."
      >
        <Toggle
          checked={prefs.allowDrag}
          onCheckedChange={(checked) => setPrefs({ allowDrag: checked })}
          aria-label="Permitir arrastar entregas"
        />
      </SettingsRow>
    </div>
  );
}

function FocusSection() {
  const { prefs, setPrefs } = usePreferences();
  return (
    <div>
      <HowItWorks title="Como funciona?">
        O kit bloqueia horário quando a semana fica abaixo do mínimo de foco. Aqui a faixa escolhida
        (manhã ou tarde) aparece sombreada na grade, para a operação não marcar entrega em cima.
      </HowItWorks>
      <SettingsRow
        title="Proteção de foco"
        description="Marca uma faixa fixa nos dias de expediente."
      >
        <Toggle
          checked={prefs.focusGuardEnabled}
          onCheckedChange={(checked) => setPrefs({ focusGuardEnabled: checked })}
          aria-label="Proteção de foco"
        />
      </SettingsRow>
      <div className="flex flex-wrap items-center gap-2 py-4 text-[14px] text-[var(--color-ink)]">
        <span>Bloquear</span>
        <MenuSelect
          aria-label="Quando bloquear foco"
          value={prefs.focusWhen}
          onChange={(next) => setPrefs({ focusWhen: next as CalendarPreferences["focusWhen"] })}
          options={[
            { value: "morning", label: "de manhã" },
            { value: "afternoon", label: "à tarde" },
          ]}
        />
        <span>quando a semana tiver menos de</span>
        <MenuSelect
          aria-label="Mínimo de horas de foco"
          value={String(prefs.minFocusHours)}
          onChange={(next) => setPrefs({ minFocusHours: Number(next) as 4 | 8 | 12 })}
          options={[
            { value: "4", label: "4 horas" },
            { value: "8", label: "8 horas" },
            { value: "12", label: "12 horas" },
          ]}
        />
        <span>de foco.</span>
      </div>
    </div>
  );
}

function LinksSection() {
  return (
    <div>
      <p className="mb-4 max-w-xl text-[14px] leading-5 text-[var(--color-ink-muted)]">
        Cada cliente entra no próprio calendário em /@handle, com senha de 4 dígitos. Não há uma aba
        extra de links: o portal vive no cadastro do cliente.
      </p>
      <Link
        className="inline-flex h-10 items-center rounded-lg bg-[#0074FF] px-4 text-[14px] font-semibold text-white"
        to="/clientes"
      >
        Abrir clientes
      </Link>
    </div>
  );
}

function ProfileSection() {
  const { prefs, setPrefs } = usePreferences();
  const me = useQuery({
    queryKey: ["me"],
    queryFn: async () => unwrap(await api.api.me.get()) as { email: string; userId: string },
  });
  const catalog = useAdminCatalog();
  const fallback = me.data?.email.split("@")[0] ?? "Equipe";

  return (
    <div>
      <SettingsRow title="E-mail" description="Conta operacional. Não há cadastro público.">
        <p className="text-right text-[13px] font-semibold text-[var(--color-ink)]">
          {me.data?.email ?? "…"}
        </p>
      </SettingsRow>
      <SettingsRow title="Nome" description="Aparece no rodapé da sidebar e no avatar.">
        <Input
          aria-label="Nome"
          placeholder="Nome"
          value={prefs.firstName}
          onChange={(event) => {
            const firstName = event.target.value;
            setPrefs({
              firstName,
              displayName: displayNameFromParts(firstName, prefs.lastName, fallback),
            });
          }}
        />
      </SettingsRow>
      <SettingsRow title="Sobrenome" description="Completa o nome exibido na sidebar.">
        <Input
          aria-label="Sobrenome"
          placeholder="Sobrenome"
          value={prefs.lastName}
          onChange={(event) => {
            const lastName = event.target.value;
            setPrefs({
              lastName,
              displayName: displayNameFromParts(prefs.firstName, lastName, fallback),
            });
          }}
        />
      </SettingsRow>
      <SettingsRow title="Equipe" description="Quem pode ser responsável por uma entrega.">
        <p className="text-right text-[13px] font-medium text-[var(--color-ink-muted)]">
          {catalog.team.length} {catalog.team.length === 1 ? "pessoa" : "pessoas"}
        </p>
      </SettingsRow>
      <div className="mt-4 flex items-center gap-2 text-[13px] text-[var(--color-ink-muted)]">
        <IconClients className="size-4" />
        {catalog.clients.length} clientes neste workspace.
      </div>
    </div>
  );
}
